import { DialogTitle, Dialog, DialogActions, DialogContent, DialogContentText } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdArrowBack, MdDownload, MdSchool } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { Button } from "rsuite";
import axios from "axios";
import { web_url } from "../../constant";

function TranslatorSchoolDownload() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [schools, setSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [excelDownloading, setExcelDownloading] = useState(false);
    const [pdfDownloading, setPdfDownloading] = useState(false);
    const [openError, setOpenError] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [statsData, setStatsData] = useState([]);

    const handleCloseError = () => setOpenError(false);

    const getLogDetails = () =>
        JSON.parse(localStorage.getItem("logDetails")) || JSON.parse(sessionStorage.getItem("logDetails"));

    useEffect(() => {
        const logDetails = getLogDetails();
        if (!logDetails) return;
        const config = { headers: { Authorization: `Bearer ${logDetails.token}` } };

        Promise.all([
            axios.get(`${web_url}/api/auth/schools`, config),
            axios.get(`${web_url}/api/email-threads/school-letter-stats`, config)
        ])
            .then(([schoolRes, statsRes]) => {
                setSchools(schoolRes.data);
                setStatsData(statsRes.data || []);
                if (schoolRes.data.length > 0) setSelectedSchool(schoolRes.data[0]);
                setIsLoading(false);
            })
            .catch(() => setIsLoading(false));
    }, []);

    const handleDownloadExcel = () => {
        if (!selectedSchool) return;
        const logDetails = getLogDetails();
        if (!logDetails) return;
        setExcelDownloading(true);
        const config = {
            headers: { Authorization: `Bearer ${logDetails.token}` },
            responseType: "blob",
        };
        axios.get(`${web_url}/api/email-threads/school-letters-excel?school=${encodeURIComponent(selectedSchool.name)}&includeSummary=false`, config)
            .then((res) => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url;
                a.download = `${selectedSchool.name.replace(/\s+/g, "_")}_letters.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            })
            .catch(() => {
                setErrorMsg("Failed to download school letters. Please try again.");
                setOpenError(true);
            })
            .finally(() => setExcelDownloading(false));
    };

    const handleDownloadPdf = () => {
        if (!selectedSchool) return;
        const logDetails = getLogDetails();
        if (!logDetails) return;
        setPdfDownloading(true);
        const config = { headers: { Authorization: `Bearer ${logDetails.token}` } };
        
        // Open the print window synchronously to prevent popup blockers
        const printWindow = window.open("", "_blank");
        if (!printWindow) {
            setErrorMsg("Please allow popups to download the PDF.");
            setOpenError(true);
            setPdfDownloading(false);
            return;
        }
        printWindow.document.write("<html><head><title>Loading Report...</title></head><body><h2 style='font-family:sans-serif;text-align:center;margin-top:50px;color:#7A5E00;'>Generating Report...</h2></body></html>");

        axios.get(`${web_url}/api/email-threads/school-letter-stats`, config).then((statsRes) => {
            const schoolStats = statsRes.data.find(s => s.schoolName === selectedSchool.name);
            if (!schoolStats || !schoolStats.studentsSubmitted) {
                setErrorMsg("No students found for this school.");
                setOpenError(true);
                setPdfDownloading(false);
                return;
            }
            const allStudents = [
                ...(schoolStats.studentsSubmitted || []),
                ...(schoolStats.studentsNotSubmitted || [])
            ];
            const schoolEmails = new Set(allStudents.map(s => s.studentEmail));
            const emailToName = {};
            allStudents.forEach(s => { emailToName[s.studentEmail] = s.studentName || s.studentEmail; });

            axios.post(`${web_url}/api/email-threads/by-participants`, Array.from(schoolEmails), config).then((threadsRes) => {
                const letters = threadsRes.data.flatMap(thread => {
                    const receiverInSchool = thread.participants?.find(p => schoolEmails.has(p));
                    if (!receiverInSchool) return [];
                    return thread.messages
                        .filter(msg => msg.type === "PENPAL" && msg.isDraft === "FALSE" && msg.sender !== receiverInSchool)
                        .map(msg => ({ ...msg, receiver: receiverInSchool }));
                });

            const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
            const formatDate = (ts) => ts ? new Date(ts).toLocaleString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

            const detectLanguage = (text) => {
                if (!text) return "";
                let sinhalaCount = 0;
                let tamilCount = 0;
                for (let i = 0; i < text.length; i++) {
                    const code = text.charCodeAt(i);
                    if (code >= 0x0D80 && code <= 0x0DFF) sinhalaCount++;
                    if (code >= 0x0B80 && code <= 0x0BFF) tamilCount++;
                }
                if (sinhalaCount > tamilCount) return "Sinhala";
                if (tamilCount > sinhalaCount) return "Tamil";
                return "";
            };

            let bodyContent = "";
            letters.forEach((msg, i) => {
                const originalBody = stripHtml(msg.body);
                const englishBody = stripHtml(msg.englishBody);
                const translatedBody = stripHtml(msg.translatedBody);

                const detectedOriginalLang = detectLanguage(msg.body) || msg.language || "";
                const detectedTransLang = detectLanguage(msg.translatedBody) || msg.language || "";

                bodyContent += `
                    <div class="letter" style="${i > 0 ? 'page-break-before: always;' : ''}">
                        <h2 style="font-size:16px;color:#A07B00;margin-bottom:8px;">Letter ${i + 1}</h2>
                        <table style="font-size:12px;margin-bottom:12px;border-collapse:collapse;">
                            <tr><td style="padding:3px 10px 3px 0;font-weight:bold;color:#555;">From:</td><td>${msg.sender}</td></tr>
                            <tr><td style="padding:3px 10px 3px 0;font-weight:bold;color:#555;">To:</td><td>${emailToName[msg.receiver] || msg.receiver} (${msg.receiver})</td></tr>
                            <tr><td style="padding:3px 10px 3px 0;font-weight:bold;color:#555;">Date:</td><td>${formatDate(msg.sentAt)}</td></tr>
                            <tr><td style="padding:3px 10px 3px 0;font-weight:bold;color:#555;">Status:</td><td>${msg.status || ""}</td></tr>
                            <tr><td style="padding:3px 10px 3px 0;font-weight:bold;color:#555;">Language:</td><td>${detectedOriginalLang}</td></tr>
                        </table>
                        ${originalBody ? `<div style="margin-bottom:14px;"><p style="font-size:13px;font-weight:bold;color:#1A5276;margin-bottom:4px;">Original Letter (${detectedOriginalLang || "Sinhala/Tamil"}):</p><div style="font-size:12px;background:#F8F9FA;border-left:3px solid #1A5276;padding:10px 14px;line-height:1.7;white-space:pre-wrap;">${originalBody}</div></div>` : ""}
                        ${translatedBody ? `<div style="margin-bottom:14px;"><p style="font-size:13px;font-weight:bold;color:#C0392B;margin-bottom:4px;">Translated Version (${detectedTransLang || "Sinhala/Tamil"}):</p><div style="font-size:12px;background:#FFF5F5;border-left:3px solid #C0392B;padding:10px 14px;line-height:1.7;white-space:pre-wrap;">${translatedBody}</div></div>` : ""}
                    </div>
                `;
            });

            const fullHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>School Letter Report - ${selectedSchool.name}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Sinhala&family=Noto+Sans+Tamil&family=Noto+Sans&display=swap');
body { font-family: 'Noto Sans', 'Noto Sans Sinhala', 'Noto Sans Tamil', Arial, sans-serif; padding: 20px; color: #333; }
.letter { page-break-inside: avoid; }
</style></head><body>
<h1 style="font-size:22px;color:#7A5E00;border-bottom:2px solid #FDC50C;padding-bottom:10px;">School Letter Report — ${selectedSchool.name}</h1>
<p style="font-size:13px;color:#666;">Total letters: ${letters.length} | Generated: ${new Date().toLocaleString()}</p>
<hr style="border:1px solid #eee;margin:20px 0;" />
${bodyContent}
<script>setTimeout(function(){ window.print(); }, 500);<\/script>
</body></html>`;

            if (printWindow) {
                printWindow.document.open();
                printWindow.document.write(fullHtml);
                printWindow.document.close();
            }
            setPdfDownloading(false);
            }).catch(() => {
                setErrorMsg("Failed to generate PDF. Please try again.");
                setOpenError(true);
                setPdfDownloading(false);
            });
        }).catch(() => {
            setErrorMsg("Failed to generate PDF. Please try again.");
            setOpenError(true);
            setPdfDownloading(false);
        });
    };

    return (
        <>
            <div className={`${i18n.language === "en" ? "font-urban" : i18n.language === "si" ? "font-noto" : "font-mukta"} overflow-x-hidden xl:pt-[20px] min-h-screen pb-[120px]`}>
                <div className="pt-[100px] md:pt-[130px] lg:pt-[150px] xl:pt-[0px] w-full">
                    <div className="ml-[30px] md:ml-[60px] lg:ml-[80px] xl:ml-[80px] flex items-center">
                        <MdArrowBack size={25} className="md:hidden cursor-pointer" onClick={() => navigate("/mobile/translator/dashboard")} />
                        <MdArrowBack size={30} className="hidden md:block xl:hidden cursor-pointer" onClick={() => navigate("/mobile/translator/dashboard")} />
                        <MdArrowBack size={30} className="hidden xl:block cursor-pointer" onClick={() => navigate("/desktop/translator/dashboard")} />
                        <p className="text-[20px] md:text-[23px] lg:text-[25px] xl:text-[20px] 2xl:text-[22px] font-bold px-[10px] py-[5px] md:px-[13px] md:py-[8px]">
                            School Letter Downloads
                        </p>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center pt-[60px]">
                        <div className="w-10 h-10 border-2 border-t-transparent border-[#FFA803] rounded-full animate-spin"></div>
                    </div>
                ) : schools.length === 0 ? (
                    <div className="flex flex-col justify-center items-center pt-[60px] px-[30px]">
                        <MdSchool size={60} className="text-gray-300 mb-[16px]" />
                        <p className="text-gray-500 text-[16px] text-center">No schools are assigned to your account yet.</p>
                        <p className="text-gray-400 text-[13px] text-center mt-[6px]">Please contact your researcher to get schools assigned.</p>
                    </div>
                ) : (
                    <>
                        <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-[24px]">
                            <p className="text-[13px] md:text-[14px] text-gray-500 mb-[6px] font-medium">
                                Select a school to download all letters received by its students. Each download includes original and translated versions.
                            </p>
                        </div>

                        <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-[12px]">
                            <label className="block text-[14px] md:text-[16px] font-semibold text-gray-700 mb-[6px]">
                                Select School
                            </label>
                            <div className="relative w-full md:w-[400px]">
                                <input
                                    type="text"
                                    value={isDropdownOpen ? searchTerm : (selectedSchool?.name || "")}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setIsDropdownOpen(true);
                                    }}
                                    onFocus={(e) => {
                                        setSearchTerm(selectedSchool?.name || "");
                                        setIsDropdownOpen(true);
                                        e.target.select();
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => setIsDropdownOpen(false), 200);
                                    }}
                                    placeholder="Type to search school..."
                                    className="w-full border border-gray-300 rounded-md p-[10px] text-[14px] md:text-[16px] bg-white text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#FDC50C]"
                                />
                                {isDropdownOpen && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                        {schools.filter(s => s.name.toLowerCase().includes((searchTerm === (selectedSchool?.name || "") ? "" : searchTerm).toLowerCase())).map((s) => (
                                            <div 
                                                key={s.id} 
                                                className="p-[10px] hover:bg-gray-100 cursor-pointer text-[14px] md:text-[16px] text-gray-800"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    setSelectedSchool(s);
                                                    setSearchTerm(s.name);
                                                    setIsDropdownOpen(false);
                                                    if (document.activeElement) {
                                                        document.activeElement.blur();
                                                    }
                                                }}
                                            >
                                                {s.name}
                                            </div>
                                        ))}
                                        {schools.filter(s => s.name.toLowerCase().includes((searchTerm === (selectedSchool?.name || "") ? "" : searchTerm).toLowerCase())).length === 0 && (
                                            <div className="p-[10px] text-gray-500 text-[14px] md:text-[16px]">No matching schools found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {selectedSchool && (() => {
                            const schoolStats = statsData.find(s => s.schoolName === selectedSchool.name);
                            const submittedCount = schoolStats ? schoolStats.studentsSubmitted.length : 0;
                            const notSubmittedCount = schoolStats ? schoolStats.studentsNotSubmitted.length : 0;
                            const totalLetters = schoolStats ? schoolStats.totalLetters : 0;
                            return (
                            <>
                                <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-[24px] grid grid-cols-2 md:grid-cols-4 gap-[16px]">
                                    <div className="bg-[#FFF4CE] border border-[#FDC50C] rounded-xl p-[20px] flex flex-col items-center">
                                        <p className="text-[13px] md:text-[15px] text-gray-600 font-medium text-center">Total Letters Uploaded</p>
                                        <p className="text-[42px] md:text-[52px] font-bold text-[#7A5E00] mt-[4px]">{totalLetters}</p>
                                    </div>
                                    <div className="bg-[#DBFAE8] border border-[#A1E0BB] rounded-xl p-[20px] flex flex-col items-center">
                                        <p className="text-[13px] md:text-[15px] text-gray-600 font-medium text-center">Students Submitted</p>
                                        <p className="text-[42px] md:text-[52px] font-bold text-[#336C4B] mt-[4px]">{submittedCount}</p>
                                    </div>
                                    <div className="bg-[#FFE8E8] border border-[#F4AAAA] rounded-xl p-[20px] flex flex-col items-center">
                                        <p className="text-[13px] md:text-[15px] text-gray-600 font-medium text-center">Students Not Submitted</p>
                                        <p className="text-[42px] md:text-[52px] font-bold text-[#A33535] mt-[4px]">{notSubmittedCount}</p>
                                    </div>
                                    <div className="bg-[#EAF0FB] border border-[#9BB5E8] rounded-xl p-[20px] flex flex-col items-center">
                                        <p className="text-[13px] md:text-[15px] text-gray-600 font-medium text-center">Total Students in School</p>
                                        <p className="text-[42px] md:text-[52px] font-bold text-[#1A3A7A] mt-[4px]">{submittedCount + notSubmittedCount}</p>
                                    </div>
                                </div>

                                <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-[16px] bg-[#FFF4CE] border border-[#FDC50C] rounded-xl p-[20px]">
                                    <p className="font-bold text-[15px] md:text-[17px] text-gray-800 mb-[4px]">{selectedSchool.name}</p>
                                    <p className="text-[12px] md:text-[13px] text-gray-500">{selectedSchool.district}</p>
                                    <p className="text-[12px] md:text-[13px] text-gray-600 mt-[8px]">
                                        Download includes: <strong>Original letters</strong> and <strong>Sinhala/Tamil translations</strong> received by students in this school.
                                    </p>
                                </div>

                                <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-[20px] flex flex-wrap gap-[12px]">
                                    <button
                                        id="translator-download-excel-btn"
                                        disabled={excelDownloading}
                                        onClick={handleDownloadExcel}
                                        className="flex items-center gap-[8px] bg-[#217346] text-white rounded-md text-[14px] md:text-[15px] py-[10px] px-[20px] hover:bg-opacity-90 disabled:opacity-50"
                                    >
                                        {excelDownloading ? (
                                            <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
                                        ) : (
                                            <MdDownload size={18} />
                                        )}
                                        Download as Excel (.xlsx)
                                    </button>
                                    <button
                                        id="translator-download-pdf-btn"
                                        disabled={pdfDownloading}
                                        onClick={handleDownloadPdf}
                                        className="flex items-center gap-[8px] bg-[#C0392B] text-white rounded-md text-[14px] md:text-[15px] py-[10px] px-[20px] hover:bg-opacity-90 disabled:opacity-50"
                                    >
                                        {pdfDownloading ? (
                                            <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
                                        ) : (
                                            <MdDownload size={18} />
                                        )}
                                        Download as PDF
                                    </button>
                                </div>

                                <div className="mx-[20px] md:mx-[80px] xl:mx-[120px] mt-[30px] bg-white border border-gray-200 rounded-xl p-[20px]">
                                    <p className="font-semibold text-[14px] md:text-[15px] text-gray-700 mb-[12px]">What's included in each format?</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-[12px]">
                                        <div className="bg-[#EAF4EE] rounded-lg p-[14px]">
                                            <p className="font-bold text-[13px] text-[#217346] mb-[6px]">📊 Excel (.xlsx)</p>
                                            <ul className="text-[12px] text-gray-600 space-y-[3px] list-disc ml-[14px]">
                                                <li>Sheet 1: Original Letters (all students)</li>
                                                <li>Sheet 2: Translated Letters (Sinhala/Tamil)</li>
                                            </ul>
                                        </div>
                                        <div className="bg-[#FDEDEC] rounded-lg p-[14px]">
                                            <p className="font-bold text-[13px] text-[#C0392B] mb-[6px]">📄 PDF</p>
                                            <ul className="text-[12px] text-gray-600 space-y-[3px] list-disc ml-[14px]">
                                                <li>Cover page with school name &amp; count</li>
                                                <li>One page per letter with sender, date, status</li>
                                                <li>Original letter (Sinhala/Tamil)</li>
                                                <li>English version</li>
                                                <li>Translated version (Sinhala/Tamil)</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </>
                            );
                        })()}
                    </>
                )}
            </div>

            <Dialog
                open={openError}
                onClose={(event, reason) => { if (reason !== "backdropClick") handleCloseError(); }}
                aria-labelledby="trans-download-error-title"
                aria-describedby="trans-download-error-desc"
                className="backdrop-blur-sm"
            >
                <DialogTitle id="trans-download-error-title" className="text-2xl font-bold text-gray-800 border-b border-gray-300 pb-2">Error</DialogTitle>
                <DialogContent className="bg-gray-50">
                    <DialogContentText id="trans-download-error-desc" className="text-gray-600 text-md mt-2">{errorMsg}</DialogContentText>
                </DialogContent>
                <DialogActions className="px-4 py-2">
                    <Button onClick={handleCloseError} style={{ backgroundColor: "#F2AC0E", color: "white", fontWeight: "bold" }}>OK</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default TranslatorSchoolDownload;
